import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface OpeningBalanceEntry {
  id: string;
  entryType: 'ACCOUNT' | 'CUSTOMER' | 'SUPPLIER';
  entryDate: string;
  accountId?: string;
  customerId?: string;
  supplierId?: string;
  amount: string;
  currency: string;
  notes?: string;
  isPosted: boolean;
  postedAt?: string;
  account?: { code: string; name: string };
  customer?: { name: string };
  supplier?: { name: string };
}

interface Account {
  id: string;
  code: string;
  name: string;
}

interface Customer {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
}

const OpeningBalances: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [entries, setEntries] = useState<OpeningBalanceEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<OpeningBalanceEntry | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string>('');
  const [filterPosted, setFilterPosted] = useState<string>('');

  const [formData, setFormData] = useState({
    entryType: 'ACCOUNT' as 'ACCOUNT' | 'CUSTOMER' | 'SUPPLIER',
    entryDate: new Date().toISOString().split('T')[0],
    accountId: '',
    customerId: '',
    supplierId: '',
    amount: '',
    currency: 'GBP',
    notes: '',
  });

  const [reportData, setReportData] = useState<any>(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchEntries();
    fetchAccounts();
    fetchCustomers();
    fetchSuppliers();
  }, [filterType, filterPosted]);

  const fetchEntries = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (filterType) params.entryType = filterType;
      if (filterPosted) params.isPosted = filterPosted === 'posted';
      const response = await axios.get('/api/erp/opening-balances', { params });
      setEntries(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching opening balances:', err);
      toast.error('Failed to load opening balances');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts');
      setAccounts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/client-organizations');
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/erp/suppliers');
      setSuppliers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload: any = {
        entryType: formData.entryType,
        entryDate: formData.entryDate,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        notes: formData.notes || undefined,
      };

      if (formData.entryType === 'ACCOUNT') {
        payload.accountId = formData.accountId;
      } else if (formData.entryType === 'CUSTOMER') {
        payload.customerId = formData.customerId;
      } else if (formData.entryType === 'SUPPLIER') {
        payload.supplierId = formData.supplierId;
      }

      await axios.post('/api/erp/opening-balances', payload);
      toast.success('Opening balance entry created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchEntries();
    } catch (err: any) {
      console.error('Error creating opening balance:', err);
      toast.error(err.response?.data?.error || 'Failed to create opening balance entry');
    }
  };

  const handlePost = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Please select entries to post');
      return;
    }

    if (!confirm(`Are you sure you want to post ${selectedEntries.length} opening balance entry(ies)? This will create GL entries.`)) {
      return;
    }

    try {
      await axios.post('/api/erp/opening-balances/post', { entryIds: selectedEntries });
      toast.success('Opening balance entries posted successfully');
      setSelectedEntries([]);
      fetchEntries();
    } catch (err: any) {
      console.error('Error posting opening balances:', err);
      toast.error(err.response?.data?.error || 'Failed to post opening balances');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this opening balance entry?')) {
      return;
    }

    try {
      await axios.delete(`/api/erp/opening-balances/${id}`);
      toast.success('Opening balance entry deleted successfully');
      fetchEntries();
    } catch (err: any) {
      console.error('Error deleting opening balance:', err);
      toast.error(err.response?.data?.error || 'Failed to delete opening balance entry');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await axios.post('/api/erp/opening-balances/report', {
        asOfDate: reportDate,
      });
      setReportData(response.data.data);
      setShowReportModal(true);
    } catch (err: any) {
      console.error('Error generating report:', err);
      toast.error(err.response?.data?.error || 'Failed to generate opening balance report');
    }
  };

  const resetForm = () => {
    setFormData({
      entryType: 'ACCOUNT',
      entryDate: new Date().toISOString().split('T')[0],
      accountId: '',
      customerId: '',
      supplierId: '',
      amount: '',
      currency: 'GBP',
      notes: '',
    });
  };

  const toggleSelection = (id: string) => {
    setSelectedEntries((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view opening balances.</p>
      </div>
    );
  }

  const unpostedEntries = entries.filter((e) => !e.isPosted);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Opening Balances</h1>
            <PageHelp pageIdentifier="opening-balances" title="Opening Balances" />
          </div>
          <p className="text-gray-600 mt-1">Set opening balances for accounts, customers, and suppliers</p>
        </div>
        <div className="flex gap-2">
          {hasPermission('erp_financial_report', 'read') && (
            <button
              onClick={handleGenerateReport}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Generate Report
            </button>
          )}
          {hasPermission('erp_financial_report', 'write') && (
            <>
              {unpostedEntries.length > 0 && (
                <button
                  onClick={handlePost}
                  disabled={selectedEntries.length === 0}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  Post Selected ({selectedEntries.length})
                </button>
              )}
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Create Entry
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-4 flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Type
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">All Types</option>
            <option value="ACCOUNT">Account</option>
            <option value="CUSTOMER">Customer</option>
            <option value="SUPPLIER">Supplier</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Status
          </label>
          <select
            value={filterPosted}
            onChange={(e) => setFilterPosted(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No opening balance entries found. Create your first entry to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {hasPermission('erp_financial_report', 'write') && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedEntries.length === unpostedEntries.length && unpostedEntries.length > 0}
                      onChange={() => {
                        if (selectedEntries.length === unpostedEntries.length) {
                          setSelectedEntries([]);
                        } else {
                          setSelectedEntries(unpostedEntries.map((e) => e.id));
                        }
                      }}
                      className="rounded"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account/Customer/Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  {hasPermission('erp_financial_report', 'write') && (
                    <td className="px-4 py-2">
                      {!entry.isPosted && (
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => toggleSelection(entry.id)}
                          className="rounded"
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2">{entry.entryType}</td>
                  <td className="px-4 py-2">{new Date(entry.entryDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    {entry.account && `${entry.account.code} - ${entry.account.name}`}
                    {entry.customer && entry.customer.name}
                    {entry.supplier && entry.supplier.name}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {parseFloat(entry.amount).toLocaleString('en-GB', { style: 'currency', currency: entry.currency || 'GBP' })}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      entry.isPosted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {entry.isPosted ? 'Posted' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      {!entry.isPosted && hasPermission('erp_financial_report', 'write') && (
                        <button
                          onClick={() => handleEdit(entry)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {!entry.isPosted && hasPermission('erp_financial_report', 'delete') && (
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Create Opening Balance Entry</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Type *</label>
                  <select
                    value={formData.entryType}
                    onChange={(e) => setFormData({ ...formData, entryType: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="ACCOUNT">Account</option>
                    <option value="CUSTOMER">Customer</option>
                    <option value="SUPPLIER">Supplier</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date *</label>
                  <input
                    type="date"
                    value={formData.entryDate}
                    onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                {formData.entryType === 'ACCOUNT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
                    <select
                      value={formData.accountId}
                      onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} - {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.entryType === 'CUSTOMER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                    <select
                      value={formData.customerId}
                      onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.entryType === 'SUPPLIER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                    <select
                      value={formData.supplierId}
                      onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Edit Opening Balance Entry</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Type *</label>
                  <select
                    value={formData.entryType}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled
                  >
                    <option value="ACCOUNT">Account</option>
                    <option value="CUSTOMER">Customer</option>
                    <option value="SUPPLIER">Supplier</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Entry type cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date *</label>
                  <input
                    type="date"
                    value={formData.entryDate}
                    onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                {formData.entryType === 'ACCOUNT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
                    <select
                      value={formData.accountId}
                      onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} - {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.entryType === 'CUSTOMER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                    <select
                      value={formData.customerId}
                      onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.entryType === 'SUPPLIER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                    <select
                      value={formData.supplierId}
                      onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEntry(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && reportData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Opening Balance Report</h2>
            <p className="text-gray-600 mb-4">As of {new Date(reportData.asOfDate).toLocaleDateString()}</p>

            <div className="space-y-6">
              {reportData.accountBalances.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Account Balances</h3>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.accountBalances.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td className="px-4 py-2">{item.account?.code} - {item.account?.name}</td>
                          <td className="px-4 py-2 text-right">
                            {parseFloat(item.amount).toLocaleString('en-GB', { style: 'currency', currency: item.currency || 'GBP' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {reportData.customerBalances.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Customer Balances</h3>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.customerBalances.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td className="px-4 py-2">{item.customer?.name}</td>
                          <td className="px-4 py-2 text-right">
                            {parseFloat(item.amount).toLocaleString('en-GB', { style: 'currency', currency: item.currency || 'GBP' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {reportData.supplierBalances.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Supplier Balances</h3>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.supplierBalances.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td className="px-4 py-2">{item.supplier?.name}</td>
                          <td className="px-4 py-2 text-right">
                            {parseFloat(item.amount).toLocaleString('en-GB', { style: 'currency', currency: item.currency || 'GBP' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex justify-between font-semibold">
                  <span>Grand Total:</span>
                  <span>
                    {parseFloat(reportData.totals.grandTotal).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Generation Modal */}
      {!showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ display: 'none' }}>
          {/* This is just a placeholder - the actual report generation happens on button click */}
        </div>
      )}
    </div>
  );
};

export default OpeningBalances;
